import { useState, useRef, useEffect } from 'react'

// ── Lazy-loading <img> with an opacity fade-in when the bitmap is decoded ─
// Drop-in replacement for <img>. Caches the loaded state so re-renders don't
// re-fade. If the image is already in the browser cache, the `onLoad` event
// still fires after mount, but quickly, so the fade is barely visible.
//
// Pass `priority` for an above-the-fold / LCP image (e.g. a page's hero backdrop):
// it loads eagerly with fetchpriority=high instead of the default lazy, so the
// browser fetches it immediately rather than deferring it and delaying LCP
// (2026-07-06 perf audit).
export function FadeImg({ src, alt = '', className = '', style, priority = false, ...rest }) {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef(null)

  // Handle cached images: if `complete` is already true at mount, mark loaded
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [src])

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      decoding="async"
      draggable="false"
      onLoad={() => setLoaded(true)}
      className={`img-fade ${loaded ? 'is-loaded' : ''} ${className}`}
      style={style}
      {...rest}
    />
  )
}
